import { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, money, fmtDate, fmtDateTime, errMsg } from "@/lib/api";

export default function SendInvoiceDialog({ open, onOpenChange, invoice, account, onSent }) {
  const [busy, setBusy] = useState(false);
  const email = (account?.email || "").trim();
  const phone = (account?.phone || "").replace(/[^\d]/g, "");
  const waNumber = phone.length >= 10 ? (phone.startsWith("90") ? phone : `90${phone.replace(/^0/, "")}`) : "";
  const sends = (invoice.sends || []).slice(-5).reverse();
  const CH = { email: "E-posta", whatsapp: "WhatsApp", reminder: "Hatırlatma" };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${invoice.id}/send-email`);
      toast.success(`Fatura ${r.data.to} adresine gönderildi`);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const openWhatsapp = () => {
    window.open(`https://wa.me/${waNumber}?text=${waText}`, "_blank");
    api.post(`/invoices/${invoice.id}/log-whatsapp`).then(() => onSent?.()).catch(() => {});
  };

  const waText = encodeURIComponent(
    `Sayın ${invoice.account_name},\n${invoice.no} numaralı faturanız:\n` +
    `Tarih: ${fmtDate(invoice.date)}\nVade: ${fmtDate(invoice.due_date)}\n` +
    `Ara Toplam: ${money(invoice.subtotal, invoice.currency)}\nKDV: ${money(invoice.vat_total, invoice.currency)}\n` +
    `Genel Toplam: ${money(invoice.total, invoice.currency)}`
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="send-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PaperPlaneTilt size={18} /> Faturayı Gönder</DialogTitle>
          <DialogDescription>{invoice.no} · {invoice.account_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-border p-4">
            <div className="label-eyebrow mb-1">E-posta</div>
            <div className="mb-3 text-sm" data-testid="send-invoice-email">{email || "Cari kartında e-posta tanımlı değil"}</div>
            <Button onClick={sendEmail} disabled={!email || busy} className="w-full rounded-[4px]" data-testid="send-invoice-email-button">
              <EnvelopeSimple /> {busy ? "Gönderiliyor…" : "E-posta ile Gönder"}
            </Button>
          </div>
          <div className="border border-border p-4">
            <div className="label-eyebrow mb-1">WhatsApp</div>
            <div className="mb-3 text-sm" data-testid="send-invoice-phone">{account?.phone || "Cari kartında telefon tanımlı değil"}</div>
            <Button variant="outline" disabled={!waNumber} className="w-full rounded-[4px] border-emerald-600 text-emerald-700"
              onClick={openWhatsapp} data-testid="send-invoice-whatsapp-button">
              <WhatsappLogo /> WhatsApp'ta Aç
            </Button>
            <p className="mt-2 text-[11px] text-muted-foreground">Mesaj hazır gelir, göndermek için WhatsApp'ta onaylamanız yeterli.</p>
          </div>
          {sends.length > 0 && (
            <div className="border border-border p-4" data-testid="send-invoice-history">
              <div className="label-eyebrow mb-2">Gönderim Geçmişi</div>
              <ul className="space-y-1 text-xs">
                {sends.map((s, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{CH[s.channel] || s.channel}</span>
                    <span className="truncate">{s.to}</span>
                    <span className="font-num whitespace-nowrap">{fmtDateTime(s.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
